import { Box } from "@mui/material"
import { GlobalStateContext } from "GlobalState"
import classNames from "classnames"
import { DELIVERY_STATES } from "constants"
import { log } from "modules/logs"
import * as nativeAPI from "modules/nativeAPI"
import { useContext } from "react"
import { useNavigate } from "react-router-dom"
import css from "./AppInstallBtn.module.scss"
import CTABtn from "./CTABtn"
import ProgressBar from "./ProgressBar"

export const AppInstallBtn = ({
  appId,
  packageName,
  versionCode: proposedVersionCode,
  isPartnerApp,
  type = "default",

  app = null, // for reporting
  appCollection = null, // for reporting
  tab = null, // for reporting
}) => {
  const { apps } = useContext(GlobalStateContext)
  const navigate = useNavigate()
  let { installedVersionCode, deliveryProcessVersionCode, deliveryProcessState, progress } = apps[packageName] || {}
  let state = deliveryProcessState;

  const deliveryShowsThatGameIsInstalled = !!deliveryProcessVersionCode && deliveryProcessState === DELIVERY_STATES.installed;
  const gamesIsInstalledOnDevice = !!installedVersionCode || deliveryShowsThatGameIsInstalled;
  const currentInstalledVersionOnDevice =
    deliveryShowsThatGameIsInstalled
      ? deliveryProcessVersionCode
      : installedVersionCode;
  const gameIsCurrentlyInstalled =
    [DELIVERY_STATES.queued,
    DELIVERY_STATES.downloading,
    DELIVERY_STATES.installing,
    DELIVERY_STATES.sentToNative].includes(state);

  if (apps[packageName]) {
    if (gameIsCurrentlyInstalled) {
      log(`${packageName}: games is currently being installed`);
    } else if (deliveryShowsThatGameIsInstalled) {
      log(`${packageName}: delivery shows that game is installed with version: ${deliveryProcessVersionCode}`);
    } else if (!!installedVersionCode) {
      log(`${packageName}: we see that the game package was installed with version: ${installedVersionCode}`);
    }
  }

  if (!gameIsCurrentlyInstalled && gamesIsInstalledOnDevice && proposedVersionCode > currentInstalledVersionOnDevice) {
    log(`${packageName}: installed version code ${currentInstalledVersionOnDevice} is lower than what we have ${proposedVersionCode}. providing update option`)
    state = DELIVERY_STATES.update;
  }

  let containerWidth = type === "large" ? "100%" : 60
  const progressThickness = type === "large" ? 3.5 : 2.5
  let btnHeight = type === "large" ? 42 : 28
  let btnStyle = {
    height: btnHeight,
    borderRadius: btnHeight / 2,
    minWidth: btnHeight,
  }

  let label
  let onClickHandler
  let progressVarient
  let btnIcon

  switch (state) {
    case DELIVERY_STATES.sentToNative:
    case DELIVERY_STATES.installing:
    case DELIVERY_STATES.queued:
      progressVarient = "indeterminate"
      progress = 0
      btnStyle.background = "transparent"
      break

    case DELIVERY_STATES.downloading:
      btnIcon = "close"
      progressVarient = "determinate"
      onClickHandler = () => {
        nativeAPI.packageCancelDelivery(packageName)
      }
      btnStyle.background = "transparent"
      break

    case DELIVERY_STATES.installed:
    case DELIVERY_STATES.launched:
      if (type !== "large") {
        label = "Play"
      } else {
        label = "Play Now"
        btnIcon = "arrow-right"
      }
      onClickHandler = () => {
        nativeAPI.packageLaunch(packageName)
      }
      break
    case DELIVERY_STATES.update:
      label = "Update"
      if (type === "large") {
        btnIcon = "install"
      }
      onClickHandler = () => nativeAPI.packageDeliver(appId, packageName, true)
      break
    case DELIVERY_STATES.failed:
    case DELIVERY_STATES.error:
    case DELIVERY_STATES.cancelled:
    default:
      label = "Get"
      btnIcon = type === "small" ? null : "install"

      if (type === "large") {
        label = isPartnerApp ? "Install" : "Get it on Google Play"

        onClickHandler = () => {
          if (isPartnerApp) {
            nativeAPI.packageDeliver(appId, packageName)
          } else {
            nativeAPI.openUrl(`https://play.google.com/store/apps/details?id=${packageName}`)
          }
        }
      } else {
        onClickHandler = () => {
          navigate(`/app/${appId}`)
        }
      }
  }

  return (
    <Box
      className={classNames(css.appInstallBtn)}
      sx={{ width: containerWidth, height: btnHeight }}
    >
      <Box
        className={classNames(css.progressWrapper)}
        sx={{
          left: `calc(50% - ${btnHeight / 2}px)`,
          opacity: progressVarient ? 1 : 0,
          width: btnHeight,
          height: btnHeight,
        }}
      >
        <ProgressBar
          variant={progressVarient}
          value={progress}
          size={btnHeight}
          thickness={progressThickness}
        />
      </Box>
      <CTABtn
        className={classNames(css.ctaBtn)}
        style={{
          ...btnStyle,
          opacity: label || btnIcon ? 1 : 0,
          width: progressVarient ? btnHeight : "100%",
        }}
        onClick={onClickHandler}
        iconRight={btnIcon}
        iconSize={type === "large" ? 22 : 16}
        type={type}
      >
        {label}
      </CTABtn>
    </Box>
  )
}
